import { useContext } from 'react';
import { Context } from '../../../App';
import buildHansenSketchModel from './hansenSketchModel';
import HansenSketchEmptyState from './HansenSketchEmptyState';

const hasValue = (v) => (v || v === 0) && v !== '';
const fmt = (v, unit) => (hasValue(v) ? `${Number(v)}${unit ? ` ${unit}` : ''}` : '-');

export default function HansenSketchTopView({ model, variant }) {
  const { theme: appTheme } = useContext(Context);
  const theme = variant === 'pdf-light' ? 'light' : appTheme;
  const sketch = model || buildHansenSketchModel();
  const { raw, frame, top } = sketch;
  const { VW, VH, yGround, yBottom, xInset, frameStyle } = frame;
  const cx = top.cx;
  const cy = top.cy;
  const showAnnotations = !top.noInputs;
  const colRenderW = top.noInputs ? top.colWEmpty : top.colW;
  const stemIsOutOfBounds = !top.isStemCompletelyOnBase;
  const buildZigzagPath = (x1, y, x2, amp = 2, step = 8) => {
    const width = Math.max(0, x2 - x1);
    const n = Math.max(2, Math.floor(width / step));
    const dx = width / n;
    let d = `M ${x1} ${y}`;
    for (let i = 1; i <= n; i += 1) {
      const x = x1 + i * dx;
      const yy = i % 2 === 0 ? y - amp : y + amp;
      d += ` L ${x} ${yy}`;
    }
    return d;
  };
  const bDimY = Math.min(top.bottom + 16, yBottom - 12);
  const bTextY = Math.min(top.bottom + 30, yBottom - 2);
  const lDimTop = Math.max(top.top, yGround + 1);
  const lDimBottom = Math.min(top.bottom, yBottom - 1);
  const lDimMid = (lDimTop + lDimBottom) / 2;

  const concretePatternId = theme === 'light' ? 'ht-concrete-light' : 'ht-concrete-dark';
  const soilTint = theme === 'light' ? '#f7f2e6' : '#2f2f2f';
  const stemFill = stemIsOutOfBounds ? (theme === 'light' ? '#f8c7cc' : '#6b2b2b') : `url(#${concretePatternId})`;
  const stemStroke = stemIsOutOfBounds ? '#c62828' : '#555';
  const badgeRightGap = 5;
  const badgeW = 154;
  const badgeH = 18;
  const badgeStackGap = 4;
  const statusBadge = {
    w: badgeW,
    h: badgeH,
    x: VW - xInset - badgeW - badgeRightGap,
    y: yGround + 60,
    fill: top.isStemCompletelyOnBase ? (theme === 'light' ? '#e8f5e9' : '#274a2f') : theme === 'light' ? '#ffebee' : '#5a1f27',
    stroke: top.isStemCompletelyOnBase ? '#2e7d32' : '#c62828',
    text: top.isStemCompletelyOnBase ? 'Stem is on the base' : 'Stem is not on the base',
  };
  const lClipBadge = {
    w: badgeW,
    h: badgeH,
    x: VW - xInset - badgeW - badgeRightGap,
    y: statusBadge.y + statusBadge.h + badgeStackGap,
    fill: theme === 'light' ? '#fff8e1' : '#54451c',
    stroke: '#a67c00',
    text: 'L display clamped',
  };
  const clipBadgeText = top.displayClippedTokens.length ? `Display clipped: ${top.displayClippedTokens.join(' / ')}` : '';
  const eccClipBadge = {
    w: badgeW,
    h: badgeH,
    x: VW - xInset - badgeW - badgeRightGap,
    y: lClipBadge.y + lClipBadge.h + badgeStackGap,
    fill: theme === 'light' ? '#fff3e0' : '#5a4720',
    stroke: '#ef6c00',
    text: clipBadgeText,
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingInline: 8 }}>
      <div style={frameStyle}>
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', width: '100%', height: '100%', maxWidth: '100%' }}
        >
          <defs>
            <marker id="ht-arrow" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
              <path d="M0,0 L7,3.5 L0,7 Z" fill="currentColor" />
            </marker>
            <marker id="ht-arrow-open-r" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8" fill="none" stroke="currentColor" strokeWidth="1.2" />
            </marker>
            <marker id="ht-arrow-open-l" markerWidth="8" markerHeight="8" refX="2" refY="4" orient="auto">
              <path d="M8,0 L0,4 L8,8" fill="none" stroke="currentColor" strokeWidth="1.2" />
            </marker>
            <marker id="ht-arrow-open-r-sm" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
              <path d="M0,0 L5,2.5 L0,5" fill="none" stroke="currentColor" strokeWidth="1" />
            </marker>
            <marker id="ht-arrow-open-l-sm" markerWidth="5" markerHeight="5" refX="1" refY="2.5" orient="auto">
              <path d="M5,0 L0,2.5 L5,5" fill="none" stroke="currentColor" strokeWidth="1" />
            </marker>
            <marker id="ht-arrow-open-r-sm-green" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
              <path d="M0,0 L5,2.5 L0,5" fill="none" stroke="#2e7d32" strokeWidth="1" />
            </marker>
            <marker id="ht-arrow-open-l-sm-green" markerWidth="5" markerHeight="5" refX="1" refY="2.5" orient="auto">
              <path d="M5,0 L0,2.5 L5,5" fill="none" stroke="#2e7d32" strokeWidth="1" />
            </marker>
            <marker id="ht-arrow-open-r-sm-purple" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
              <path d="M0,0 L5,2.5 L0,5" fill="none" stroke="#6a1b9a" strokeWidth="1" />
            </marker>
            <marker id="ht-arrow-open-l-sm-purple" markerWidth="5" markerHeight="5" refX="1" refY="2.5" orient="auto">
              <path d="M5,0 L0,2.5 L5,5" fill="none" stroke="#6a1b9a" strokeWidth="1" />
            </marker>
            <pattern id="ht-soil" patternUnits="userSpaceOnUse" width="16" height="16">
              <circle cx="2" cy="2" r="1" fill="currentColor" opacity="0.2" />
              <circle cx="8" cy="6" r="1" fill="currentColor" opacity="0.2" />
              <circle cx="6" cy="13" r="1" fill="currentColor" opacity="0.2" />
            </pattern>
            <pattern id="ht-concrete-light" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#ececec" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#9e9e9e" strokeWidth="0.6" />
            </pattern>
            <pattern id="ht-concrete-dark" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#3f3f3f" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#6a6a6a" strokeWidth="0.6" />
            </pattern>
            <pattern id="ht-overflow-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="rgba(198,40,40,0.12)" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="#c62828" strokeWidth="0.9" />
            </pattern>
            <mask id="ht-overflow-mask" maskUnits="userSpaceOnUse">
              <rect x="0" y="0" width={VW} height={VH} fill="white" />
              <rect x={top.left} y={top.top} width={top.footingW} height={top.footingH} fill="black" />
            </mask>
            <clipPath id="ht-plot-clip">
              <rect x={xInset} y={yGround} width={VW - xInset * 2} height={yBottom - yGround} />
            </clipPath>
          </defs>

          <style>{`
            .ht-ln { stroke: currentColor; stroke-width: 1.1; fill: none; }
            .ht-dim { stroke: currentColor; stroke-width: 1; fill: none; }
            .ht-tx { font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial; fill: currentColor; }
            .ht9 { font-size: 9px; }
            .ht12 { font-size: 12px; }
            .ht10 { font-size: 10px; }
            .ht13 { font-size: 13px; }
            .ht-axis { stroke-dasharray: 5 4; opacity: 0.75; }
            .ht-ecc { stroke-width: 1; fill: none; opacity: 0.95; }
            .ht-clamp-edge { stroke: #666; stroke-width: 1.1; fill: none; }
          `}</style>

          <rect x={xInset} y={yGround} width={VW - xInset * 2} height={yBottom - yGround} fill={soilTint} stroke="currentColor" />
          <rect x={xInset} y={yGround} width={VW - xInset * 2} height={yBottom - yGround} fill="url(#ht-soil)" />
          <g clipPath="url(#ht-plot-clip)">
            <rect
              x={top.left}
              y={top.top}
              width={top.footingW}
              height={top.footingH}
              fill={`url(#${concretePatternId})`}
              stroke={top.isLengthClipped ? 'none' : '#666'}
            />
            {top.isLengthClipped && (
              <>
                <line className="ht-ln" x1={top.left} y1={top.top} x2={top.left} y2={top.bottom} stroke="#666" />
                <line className="ht-ln" x1={top.right} y1={top.top} x2={top.right} y2={top.bottom} stroke="#666" />
                <path className="ht-clamp-edge" d={buildZigzagPath(top.left, top.top, top.right)} />
                <path className="ht-clamp-edge" d={buildZigzagPath(top.left, top.bottom, top.right)} />
              </>
            )}
            <line className="ht-ln ht-axis" x1={top.left} y1={cy} x2={top.right} y2={cy} />
            <line className="ht-ln ht-axis" x1={cx} y1={top.top} x2={cx} y2={top.bottom} />

            <rect x={top.colCx - colRenderW / 2} y={top.colCy - top.colH / 2} width={colRenderW} height={top.colH} fill={stemFill} stroke={stemStroke} />
            {showAnnotations && stemIsOutOfBounds && (
              <rect
                x={top.colCx - colRenderW / 2}
                y={top.colCy - top.colH / 2}
                width={colRenderW}
                height={top.colH}
                fill="url(#ht-overflow-hatch)"
                mask="url(#ht-overflow-mask)"
              />
            )}
            <circle cx={top.colCx} cy={top.colCy} r="3" fill="#c62828" />
            <circle cx={cx} cy={cy} r="3" fill="currentColor" />
          </g>
          {showAnnotations && (
            <>
              <line
                className="ht-dim"
                x1={top.left}
                y1={bDimY}
                x2={top.right}
                y2={bDimY}
                markerStart="url(#ht-arrow-open-l)"
                markerEnd="url(#ht-arrow-open-r)"
              />
              <text className="ht-tx ht13" x={cx} y={bTextY} textAnchor="middle">
                B = {fmt(raw.fndWidth, 'm')}
              </text>

              <line
                className="ht-dim"
                x1={top.left - 34}
                y1={lDimTop}
                x2={top.left - 34}
                y2={lDimBottom}
                markerStart="url(#ht-arrow-open-l)"
                markerEnd="url(#ht-arrow-open-r)"
              />
              <text className="ht-tx ht13" x={top.left - 42} y={lDimMid} textAnchor="end">
                L = {fmt(raw.fndLength, 'm')}
              </text>

              {top.showEx && (
                <>
                  <line
                    className="ht-ecc"
                    x1={top.colCx}
                    y1={top.colCy}
                    x2={cx}
                    y2={top.colCy}
                    markerStart="url(#ht-arrow-open-l-sm-green)"
                    markerEnd="url(#ht-arrow-open-r-sm-green)"
                    stroke="#2e7d32"
                  />
                  <text className="ht-tx ht10" x={(cx + top.colCx) / 2} y={top.colCy - 8} textAnchor="middle" fill="#2e7d32">
                    e<tspan baselineShift="-8%">x</tspan> = {fmt(raw.eccentricityX, 'm')}
                  </text>
                </>
              )}

              {top.showEy && (
                <>
                  <line
                    className="ht-ecc"
                    x1={top.colCx}
                    y1={top.colCy}
                    x2={top.colCx}
                    y2={cy}
                    markerStart="url(#ht-arrow-open-l-sm-purple)"
                    markerEnd="url(#ht-arrow-open-r-sm-purple)"
                    stroke="#6a1b9a"
                  />
                  <text className="ht-tx ht10" x={top.colCx + 8} y={(cy + top.colCy) / 2} fill="#6a1b9a">
                    e<tspan baselineShift="-8%">y</tspan> = {fmt(raw.eccentricityY, 'm')}
                  </text>
                </>
              )}

              <line
                className="ht-dim"
                x1={top.colCx - colRenderW / 2}
                y1={top.colCy - top.colH / 2 - 14}
                x2={top.colCx + colRenderW / 2}
                y2={top.colCy - top.colH / 2 - 14}
                markerStart="url(#ht-arrow-open-l-sm)"
                markerEnd="url(#ht-arrow-open-r-sm)"
              />
              <text className="ht-tx ht10" x={top.colCx} y={top.colCy - top.colH / 2 - 20} textAnchor="middle">
                w<tspan baselineShift="-8%">x</tspan> = {fmt(raw.columnDimensionX, 'm')}
              </text>

              <line
                className="ht-dim"
                x1={top.colCx + colRenderW / 2 + 14}
                y1={top.colCy - top.colH / 2}
                x2={top.colCx + colRenderW / 2 + 14}
                y2={top.colCy + top.colH / 2}
                markerStart="url(#ht-arrow-open-l-sm)"
                markerEnd="url(#ht-arrow-open-r-sm)"
              />
              <text className="ht-tx ht10" x={top.colCx + colRenderW / 2 + 22} y={top.colCy} textAnchor="start">
                w<tspan baselineShift="-8%">y</tspan> = {fmt(raw.columnDimensionY, 'm')}
              </text>

              {top.showHb && (
                <>
                  <line
                    className="ht-ln"
                    x1={top.left}
                    y1={top.top - 10}
                    x2={top.left + 74}
                    y2={top.top - 10}
                    markerEnd="url(#ht-arrow)"
                    stroke="#2e7d32"
                  />
                  <text className="ht-tx ht12" x={top.left + 37} y={top.top - 26} fill="#2e7d32" textAnchor="middle">
                    H<tspan baselineShift="sub">B</tspan> = {fmt(raw.hb, 'kN')}
                  </text>
                </>
              )}

              {top.showHl && (
                <>
                  <line
                    className="ht-ln"
                    x1={top.right + 14}
                    y1={top.bottom - 62}
                    x2={top.right + 14}
                    y2={top.bottom}
                    markerEnd="url(#ht-arrow)"
                    stroke="#6a1b9a"
                  />
                  <text className="ht-tx ht12" x={top.right + 22} y={top.bottom - 31} fill="#6a1b9a" dominantBaseline="middle">
                    H<tspan baselineShift="sub">L</tspan> = {fmt(raw.hl, 'kN')}
                  </text>
                </>
              )}
            </>
          )}

          <text className="ht-tx ht12" x={VW - xInset - 10} y={yGround + 14} textAnchor="end" opacity="0.88">
            Top View
          </text>
          <line className="ht-ln" x1={VW - xInset - 53} y1={yGround + 51} x2={VW - xInset - 23} y2={yGround + 51} markerEnd="url(#ht-arrow)" opacity="0.88" />
          <line className="ht-ln" x1={VW - xInset - 53} y1={yGround + 51} x2={VW - xInset - 53} y2={yGround + 29} markerEnd="url(#ht-arrow)" opacity="0.88" />
          <text className="ht-tx ht12" x={VW - xInset - 19} y={yGround + 55} opacity="0.88">
            x
          </text>
          <text className="ht-tx ht12" x={VW - xInset - 59} y={yGround + 27} textAnchor="end" opacity="0.88">
            y
          </text>
          {showAnnotations && (
            <g opacity="0.96">
              <rect x={statusBadge.x} y={statusBadge.y} width={statusBadge.w} height={statusBadge.h} rx="6" fill={statusBadge.fill} stroke={statusBadge.stroke} />
              <text
                className="ht-tx ht9"
                x={statusBadge.x + statusBadge.w / 2}
                y={statusBadge.y + statusBadge.h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={statusBadge.stroke}
              >
                {statusBadge.text}
              </text>
            </g>
          )}
          {showAnnotations && top.isLengthClipped && (
            <g opacity="0.96">
              <rect x={lClipBadge.x} y={lClipBadge.y} width={lClipBadge.w} height={lClipBadge.h} rx="6" fill={lClipBadge.fill} stroke={lClipBadge.stroke} />
              <text
                className="ht-tx ht9"
                x={lClipBadge.x + lClipBadge.w / 2}
                y={lClipBadge.y + lClipBadge.h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={lClipBadge.stroke}
              >
                {lClipBadge.text}
              </text>
            </g>
          )}
          {showAnnotations && top.isStemDisplayClipped && (
            <g opacity="0.96">
              <rect x={eccClipBadge.x} y={eccClipBadge.y} width={eccClipBadge.w} height={eccClipBadge.h} rx="6" fill={eccClipBadge.fill} stroke={eccClipBadge.stroke} />
              <text
                className="ht-tx ht9"
                x={eccClipBadge.x + eccClipBadge.w / 2}
                y={eccClipBadge.y + eccClipBadge.h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={eccClipBadge.stroke}
              >
                {eccClipBadge.text}
              </text>
            </g>
          )}

          {top.noInputs && (
            <HansenSketchEmptyState VW={VW} VH={VH} theme={theme} textClassName="ht-tx ht13" />
          )}
        </svg>
      </div>
    </div>
  );
}
